import Add from "@mui/icons-material/Add";
import Close from "@mui/icons-material/Close";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { IssueListButton } from "components/Forms/IssueListButton";
import { SaveButton } from "components/SaveButton";
import { FormTextFieldSkeleton } from "components/Skeleton";
import { ExtractionStatus, Issue } from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import { ReactNode, useCallback, useEffect, useMemo } from "react";
import { Control, FormProvider, useFieldArray, useFormState, useWatch } from "react-hook-form";
import { useDeepCompareMemoize } from "use-deep-compare-effect";

import { ExtractionFieldControl } from "./ExtractionFieldControl";
import {
  ExtractionReviewFormData,
  PartialExtractionReviewFormData,
  buildBlankRow,
  buildDefaultValues,
  countFlaggedValues,
  fieldPath,
  indexConfidence,
  isEmptyReview,
  rowCellPath,
  rowGroupPath,
  toFormIssues,
  toReviewedValues,
  warnOnUnsupportedKeys,
} from "./formData";
import {
  ExtractionFieldSpec,
  ExtractionResult,
  ExtractionReviewSchema,
  ExtractionReviewValues,
  extractedBaseline,
  rowConfidenceKey,
  toDraftValue,
} from "./types";

/**
 * Below this confidence a value is flagged for the reviewer's attention.
 * Callers whose extractor is calibrated differently override it.
 */
export const DEFAULT_LOW_CONFIDENCE_THRESHOLD = 0.7;

/** A multiline field gets the full row; everything else pairs up. */
function fieldGridSize(spec: ExtractionFieldSpec) {
  return spec.kind === "multiline" ? { xs: 12 } : { xs: 12, sm: 6 };
}

export interface ExtractionReviewGridProps {
  /** The caller's field layout. Nothing about it is interpreted by the grid. */
  schema: ExtractionReviewSchema;
  /** Candidate values from whichever extractor ran. */
  extraction: ExtractionResult | null | undefined;
  /** The source `Document.extractionStatus`. */
  status: ExtractionStatus | null | undefined;
  /** The source `Document.extractionError`, shown on a failed extraction. */
  extractionError?: string | null;
  /** Receives the reviewed — not the raw — values. */
  onCommit: (reviewed: ExtractionReviewValues) => void | Promise<void>;
  onCancel?: () => void;
  /**
   * Offered from the failed state. Wire this to `useRetryDocumentExtraction`,
   * which re-queues the enrichment job and surfaces its progress in the shared
   * analyzing dialog — this component does not queue anything itself.
   */
  onRetry?: () => void;
  /**
   * The caller's validator, run as the reviewer edits. Issues are keyed in the
   * caller's own namespace — a field key, or `groupKey[index].columnKey` for a
   * row cell, the same paths the extraction pipeline emits — and land on the
   * matching input.
   */
  validator?: (reviewed: ExtractionReviewValues) => Promise<Issue[]>;
  /** Issues already known before review, keyed the same way. */
  persistedIssues?: Issue[];
  confirmLabel?: string;
  cancelLabel?: string;
  /** True while the caller's commit is in flight. */
  committing?: boolean;
  /** Confidence below which a value is flagged. Defaults to 0.7. */
  lowConfidenceThreshold?: number;
  /** Copy shown above the fields, e.g. what document the values came from. */
  description?: ReactNode;
  /**
   * True while the caller is still fetching the reading — distinct from a
   * `PENDING` status, which says the extractor is working. Keep it false on a
   * background refetch, or a poll replaces a form someone is typing in.
   */
  loading?: boolean;
}

/**
 * The shape of the form that is coming. Laid out from the caller's schema with
 * the same column rule the real fields use, so nothing jumps when they land.
 */
function ExtractionReviewSkeleton({ schema }: { schema: ExtractionReviewSchema }) {
  return (
    <Stack spacing={3} aria-busy="true" aria-live="polite">
      <Box component="span" sx={visuallyHidden}>
        Loading the extracted values
      </Box>
      {schema.sections.map((section) => (
        <Stack key={section.key} spacing={1}>
          {section.title && <Skeleton variant="text" width="12ch" />}
          <Grid container spacing={2}>
            {section.fields.map((field) => (
              <Grid key={field.key} size={fieldGridSize(field)}>
                <FormTextFieldSkeleton kind={field.kind === "multiline" ? "long" : "short"} />
              </Grid>
            ))}
          </Grid>
        </Stack>
      ))}
    </Stack>
  );
}

/**
 * The shared Document-AI review/confirm surface: extract -> review -> edit ->
 * commit, for any extractor.
 *
 * The caller supplies a field schema and a commit handler and gets back the
 * values the reviewer confirmed. Every extracted value is rendered as an
 * editable field — including the ones the extractor returned empty, so a
 * reviewer can fill in what it missed — and values the extractor was unsure
 * about are flagged until someone looks at them. Nothing here knows what the
 * values mean, so the waste-profile, bill-of-lading, data-capture, and invoice
 * ingests all reuse it unchanged.
 *
 * Laid out like the document-metadata review it shares components with: labelled
 * small fields in a responsive grid, repeated rows as per-row groups.
 */
export function ExtractionReviewGrid({
  schema,
  extraction,
  status,
  extractionError,
  onCommit,
  onCancel,
  onRetry,
  validator,
  persistedIssues,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  committing = false,
  lowConfidenceThreshold = DEFAULT_LOW_CONFIDENCE_THRESHOLD,
  description,
  loading = false,
}: ExtractionReviewGridProps) {
  // Ahead of every status check: with no reading there is no status, and the
  // fallthrough below would declare the document empty before we know.
  if (loading && extraction == null) {
    return <ExtractionReviewSkeleton schema={schema} />;
  }

  if (status === ExtractionStatus.Pending) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center" aria-live="polite">
        <CircularProgress size={20} />
        <Typography variant="body2">Reading the document…</Typography>
      </Stack>
    );
  }

  if (status === ExtractionStatus.Failed) {
    return (
      <Alert
        severity="error"
        action={
          onRetry && (
            <Button color="inherit" size="small" onClick={onRetry}>
              Try again
            </Button>
          )
        }
      >
        <AlertTitle>We couldn&apos;t read this document</AlertTitle>
        {extractionError ?? "The extraction failed, so there is nothing to review yet."}
      </Alert>
    );
  }

  if (status !== ExtractionStatus.Succeeded || extraction == null) {
    return (
      <Alert severity="info">
        Nothing has been extracted from this document yet, so there is nothing to review.
      </Alert>
    );
  }

  // Mounted only once an extraction has arrived, so the form is seeded from real
  // candidates and a pending-to-succeeded flip populates the fields without the
  // caller having to remount anything itself.
  return (
    <ExtractionReviewForm
      schema={schema}
      extraction={extraction}
      onCommit={onCommit}
      onCancel={onCancel}
      validator={validator}
      persistedIssues={persistedIssues}
      confirmLabel={confirmLabel}
      cancelLabel={cancelLabel}
      committing={committing}
      lowConfidenceThreshold={lowConfidenceThreshold}
      description={description}
    />
  );
}

type ExtractionReviewFormProps = Pick<
  ExtractionReviewGridProps,
  "schema" | "onCommit" | "onCancel" | "validator" | "persistedIssues" | "description"
> & {
  extraction: ExtractionResult;
  confirmLabel: string;
  cancelLabel: string;
  committing: boolean;
  lowConfidenceThreshold: number;
};

function ExtractionReviewForm({
  schema,
  extraction,
  onCommit,
  onCancel,
  validator,
  persistedIssues,
  confirmLabel,
  cancelLabel,
  committing,
  lowConfidenceThreshold,
  description,
}: ExtractionReviewFormProps) {
  useEffect(() => warnOnUnsupportedKeys(schema), [schema]);

  const defaultValues = useDeepCompareMemoize(buildDefaultValues(schema, extraction));
  // What the extractor itself read, which is only the same as the seed on a
  // first review — see `extractedBaseline`.
  const baseline = useMemo(() => extractedBaseline(extraction), [extraction]);
  const confidence = useMemo(() => indexConfidence(extraction.confidence), [extraction]);

  const formValidator = useCallback(
    async (data: ExtractionReviewFormData): Promise<Issue[]> => {
      if (!validator) return [];
      return toFormIssues(schema, await validator(toReviewedValues(schema, data)));
    },
    [schema, validator]
  );

  const memoizedPersistedIssues = useDeepCompareMemoize(
    toFormIssues(schema, persistedIssues ?? [])
  );

  const form = useValidatingForm<ExtractionReviewFormData>(
    defaultValues,
    memoizedPersistedIssues,
    formValidator,
    // The grid has no `stateFields`-style dynamic key indirection; issue keys are
    // already form paths by the time they get here.
    [],
    // useValidatingForm resets whenever the seed data changes. Without this, a
    // re-delivered extraction (a background refetch, a re-run) would silently
    // discard corrections the reviewer had already typed.
    { keepDirtyValuesOnReset: true }
  );
  const { control, handleSubmit, issues } = form;
  const { isSubmitting } = useFormState({ control });
  const busy = committing || isSubmitting;

  const watched: PartialExtractionReviewFormData = useWatch({ control });
  const isEmpty = isEmptyReview(watched);

  const handleConfirm = useCallback(
    () => handleSubmit((data) => onCommit(toReviewedValues(schema, data)))(),
    [handleSubmit, onCommit, schema]
  );

  return (
    <FormProvider {...form}>
      <Stack spacing={3}>
        {description && <Typography variant="body2">{description}</Typography>}

        <FlaggedCountBanner
          schema={schema}
          extraction={extraction}
          confidence={confidence}
          lowConfidenceThreshold={lowConfidenceThreshold}
        />

        {schema.sections.map((section) => (
          <Box key={section.key}>
            {section.title && (
              <Typography variant="subtitle2" mb={1.5}>
                {section.title}
              </Typography>
            )}
            <Grid container spacing={2}>
              {section.fields.map((field) => (
                <Grid key={field.key} size={fieldGridSize(field)}>
                  <ExtractionFieldControl
                    spec={field}
                    control={control}
                    path={fieldPath(field.key)}
                    extracted={toDraftValue(field, baseline.values?.[field.key])}
                    hasCandidate
                    confidence={confidence.get(field.key) ?? null}
                    lowConfidenceThreshold={lowConfidenceThreshold}
                    label={field.label}
                    disabled={busy}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}

        {(schema.rowGroups ?? []).map((group) => (
          <RowGroup
            key={group.key}
            group={group}
            extraction={extraction}
            confidence={confidence}
            lowConfidenceThreshold={lowConfidenceThreshold}
            control={control}
            disabled={busy}
          />
        ))}

        <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center">
          {issues.length > 0 && <IssueListButton issues={issues} issueDescriptor="Extraction" />}
          {onCancel && (
            <Button onClick={onCancel} disabled={busy}>
              {cancelLabel}
            </Button>
          )}
          <SaveButton
            saveText={confirmLabel}
            hideStartIcon
            loading={busy}
            loadingText={`${confirmLabel}…`}
            // An extraction that found nothing must never be committed as if it
            // had; the reviewer fills something in first.
            disabled={isEmpty}
            onClick={handleConfirm}
          />
        </Stack>
      </Stack>
    </FormProvider>
  );
}

/**
 * How many values are still showing an untouched low-confidence extraction.
 * Counted by `countFlaggedValues`, the same predicates each field's own
 * indicator uses, so the banner and the highlights cannot disagree.
 */
function FlaggedCountBanner({
  schema,
  extraction,
  confidence,
  lowConfidenceThreshold,
}: {
  schema: ExtractionReviewSchema;
  extraction: ExtractionResult;
  confidence: Map<string, number>;
  lowConfidenceThreshold: number;
}) {
  const watched: PartialExtractionReviewFormData = useWatch<ExtractionReviewFormData>();

  const flaggedCount = useMemo(
    () =>
      countFlaggedValues({
        schema,
        extraction,
        data: watched,
        confidence,
        lowConfidenceThreshold,
      }),
    [confidence, extraction, lowConfidenceThreshold, watched, schema]
  );

  if (flaggedCount === 0) return null;

  return (
    <Alert severity="warning" aria-live="polite">
      {flaggedCount === 1
        ? "1 value needs review before you confirm."
        : `${flaggedCount} values need review before you confirm.`}
    </Alert>
  );
}

function RowGroup({
  group,
  extraction,
  confidence,
  lowConfidenceThreshold,
  control,
  disabled,
}: {
  group: NonNullable<ExtractionReviewSchema["rowGroups"]>[number];
  extraction: ExtractionResult;
  confidence: Map<string, number>;
  lowConfidenceThreshold: number;
  control: Control<ExtractionReviewFormData>;
  disabled: boolean;
}) {
  const { fields, append, remove } = useFieldArray({ control, name: rowGroupPath(group.key) });
  const columnKeys = useMemo(() => group.columns.map((column) => column.key), [group]);
  const baseline = useMemo(() => extractedBaseline(extraction), [extraction]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="subtitle2">{group.title}</Typography>
        <Button
          size="small"
          startIcon={<Add />}
          disabled={disabled}
          onClick={() => append(buildBlankRow(columnKeys))}
        >
          {`Add ${group.rowNoun}`}
        </Button>
      </Stack>

      {fields.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {`No ${group.rowNoun} rows were extracted.`}
        </Typography>
      ) : (
        <Stack spacing={2}>
          {fields.map((row, index) => {
            const rowLabel = `${group.rowNoun} ${index + 1}`;
            const extractedRow =
              row.extractedIndex == null
                ? undefined
                : baseline.rows?.[group.key]?.[row.extractedIndex];

            return (
              // Named group so a repeated column stays addressable per row
              // without repeating the row in every visible field label.
              <Box
                key={row.id}
                role="group"
                aria-label={rowLabel}
                sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    {rowLabel.charAt(0).toUpperCase() + rowLabel.slice(1)}
                  </Typography>
                  <IconButton
                    size="small"
                    disabled={disabled}
                    aria-label={`Remove ${rowLabel}`}
                    title={`Remove ${rowLabel}`}
                    onClick={() => remove(index)}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Stack>
                <Grid container spacing={2}>
                  {group.columns.map((column) => {
                    const score =
                      row.extractedIndex == null
                        ? null
                        : (confidence.get(
                            rowConfidenceKey(group.key, row.extractedIndex, column.key)
                          ) ?? null);
                    return (
                      <Grid key={column.key} size={fieldGridSize(column)}>
                        <ExtractionFieldControl
                          spec={column}
                          control={control}
                          path={rowCellPath(group.key, index, column.key)}
                          extracted={toDraftValue(column, extractedRow?.[column.key])}
                          hasCandidate={row.extractedIndex != null}
                          confidence={score}
                          lowConfidenceThreshold={lowConfidenceThreshold}
                          label={column.label}
                          disabled={disabled}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
